import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Shield, Menu, X } from 'lucide-react';
import './InfoPage.css';

const PrivacyPage: React.FC = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="info-page">
      <motion.nav initial={{ y: -100 }} animate={{ y: 0 }} className="info-nav">
        <div className="nav-container">
          <div className="nav-logo" onClick={() => navigate('/')}>
            <img src="/homequest-logo.svg" alt="HomeQuest Tech" className="logo-image" />
            <div className="logo-tagline">Where Land Meets Logic</div>
          </div>
          <div className={`nav-links ${mobileMenuOpen ? 'mobile-open' : ''}`}>
            <a onClick={() => navigate('/')}>Home</a>
            <a onClick={() => navigate('/features')}>Features</a>
            <a onClick={() => navigate('/pricing')}>Pricing</a>
            <button onClick={() => navigate('/login')} className="nav-login">Sign In</button>
            <button onClick={() => navigate('/pricing')} className="nav-cta">Start Free Trial</button>
          </div>
          <button className="mobile-menu-toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X /> : <Menu />}
          </button>
        </div>
      </motion.nav>

      <section className="info-hero">
        <div className="hero-container">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="hero-badge">
              <Shield className="badge-icon" />
              <span>PRIVACY POLICY</span>
            </div>
            <h1 className="hero-title">
              Privacy <span className="gradient-text">Policy</span>
            </h1>
            <p className="hero-subtitle">
              Last updated: October 13, 2025
            </p>
          </motion.div>
        </div>
      </section>

      <section className="info-content">
        <div className="content-container">
          <h2>Our Commitment to Your Privacy</h2>
          <p>
            At HomeQuest Tech, we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our construction management platform.
          </p>

          <h2>Information We Collect</h2>
          <h3>Information You Provide</h3>
          <ul>
            <li><strong>Account Information:</strong> Name, email address, phone number, company name, and billing information</li>
            <li><strong>Project Data:</strong> CAD files, floor plans, site addresses, project details, selections, and specifications</li>
            <li><strong>Communications:</strong> Messages, emails, call recordings, and other communications through our platform</li>
            <li><strong>Vendor Information:</strong> Vendor names, contact details, bids, invoices, and performance data</li>
            <li><strong>Payment Information:</strong> Credit card details and billing addresses (processed securely through Stripe)</li>
          </ul>

          <h3>Information We Collect Automatically</h3>
          <ul>
            <li><strong>Usage Data:</strong> Pages visited, features used, time spent, and interaction patterns</li>
            <li><strong>Device Information:</strong> IP address, browser type, operating system, and device identifiers</li>
            <li><strong>Location Data:</strong> General location information for GIS county system integration</li>
            <li><strong>Cookies:</strong> We use cookies and similar technologies to enhance your experience</li>
          </ul>

          <h2>How We Use Your Information</h2>
          <p>We use the information we collect to:</p>
          <ul>
            <li>Provide and maintain our construction management platform services</li>
            <li>Process CAD files with AI to detect rooms and place selections</li>
            <li>Integrate with GIS county systems for site analysis and setback calculations</li>
            <li>Handle AI communications including calls, emails, and messages</li>
            <li>Process vendor bids and manage subcontractor relationships</li>
            <li>Analyze invoices and track project budgets with AI</li>
            <li>Generate AI-powered notes from site photos</li>
            <li>Send important notifications about your projects and account</li>
            <li>Improve our platform and develop new features</li>
            <li>Comply with legal obligations and enforce our terms</li>
          </ul>

          <h2>How We Share Your Information</h2>
          <p>We do not sell your personal information. We may share your information with:</p>
          <ul>
            <li><strong>Service Providers:</strong> Third-party vendors who help us operate our platform (cloud hosting, payment processing, AI services)</li>
            <li><strong>Vendors and Subcontractors:</strong> When you send bid requests or share project information with them</li>
            <li><strong>Team Members:</strong> Other users on your account or team who need access to collaborate</li>
            <li><strong>Legal Requirements:</strong> When required by law, court order, or to protect our rights</li>
            <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
          </ul>

          <h2>Data Security</h2>
          <p>
            We implement industry-standard security measures to protect your information:
          </p>
          <ul>
            <li><strong>Encryption:</strong> AES-256 encryption for data at rest and TLS 1.3 for data in transit</li>
            <li><strong>Access Controls:</strong> Role-based access controls and multi-factor authentication</li>
            <li><strong>Secure Storage:</strong> Data stored in SOC 2 compliant data centers with automatic backups</li>
            <li><strong>Regular Audits:</strong> Security audits and penetration testing to identify vulnerabilities</li>
            <li><strong>Monitoring:</strong> 24/7 security monitoring and intrusion detection systems</li>
          </ul>
          <p>
            However, no method of transmission over the internet is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
          </p>

          <h2>Data Retention</h2>
          <p>
            We retain your information for as long as your account is active or as needed to provide services. When you delete your account, we will delete your data within 90 days, except where we must retain it for legal compliance.
          </p>

          <h2>Your Privacy Rights</h2>
          <p>You have the right to:</p>
          <ul>
            <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
            <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
            <li><strong>Deletion:</strong> Request deletion of your personal information</li>
            <li><strong>Portability:</strong> Request your data in a portable format</li>
            <li><strong>Opt-Out:</strong> Opt out of marketing communications</li>
            <li><strong>Restrict Processing:</strong> Request that we limit how we use your information</li>
          </ul>
          <p>
            To exercise these rights, contact us at support@homequesttech.com.
          </p>

          <h2>Cookies and Tracking</h2>
          <p>
            We use cookies and similar technologies to improve your experience. You can control cookies through your browser settings, but disabling cookies may affect platform functionality.
          </p>

          <h2>SMS and Mobile Communications</h2>
          <p>
            If you opt in to receive SMS notifications from HomeQuest Tech, we will use your mobile phone number to send you messages about your projects, appointments, updates, and other construction-related notifications. By providing your phone number and checking the SMS opt-in box, you consent to receive these messages.
          </p>
          <p>
            <strong>Important SMS Privacy Notice:</strong> We will never share, sell, or rent your mobile phone number or SMS opt-in information with third parties, affiliates, or other companies for marketing or promotional purposes. Your mobile information is used solely for providing you with our construction management services and will not be used for any third-party marketing.
          </p>
          <ul>
            <li>Message frequency may vary based on your project activity</li>
            <li>Standard message and data rates may apply from your carrier</li>
            <li>You can opt out at any time by replying STOP to any message</li>
            <li>Reply HELP for assistance</li>
          </ul>

          <h2>Third-Party Services</h2>
          <p>Our platform integrates with third-party services including:</p>
          <ul>
            <li>GIS county systems for property data and zoning information</li>
            <li>Stripe for payment processing</li>
            <li>Cloud storage providers for CAD files and documents</li>
            <li>AI service providers for natural language processing</li>
            <li>Communication services for calls and emails</li>
          </ul>
          <p>
            These services have their own privacy policies, and we are not responsible for their practices.
          </p>

          <h2>Children's Privacy</h2>
          <p>
            Our platform is not intended for users under 18 years of age. We do not knowingly collect information from children.
          </p>

          <h2>Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of significant changes by email or through our platform. The "Last updated" date at the top reflects the most recent version.
          </p>

          <h2>Contact Us</h2>
          <p>
            If you have questions about this Privacy Policy, please contact us:
          </p>
          <ul>
            <li><strong>Email:</strong> support@homequesttech.com</li>
            <li><strong>Address:</strong> HomeQuest Tech, Atlanta, GA</li>
          </ul>

          <button onClick={() => navigate('/contact')} className="cta-button">
            Contact Support
          </button>
        </div>
      </section>

      <footer className="info-footer">
        <div className="footer-container">
          <div className="footer-content">
            <p>&copy; 2025 HomeQuest Tech. All rights reserved.</p>
            <div className="footer-links">
              <a onClick={() => navigate('/')}>Home</a>
              <a onClick={() => navigate('/features')}>Features</a>
              <a onClick={() => navigate('/terms')}>Terms</a>
              <a onClick={() => navigate('/security')}>Security</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPage;
